'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import styles from './NavQuickLinksVertical.module.scss';

/**
 * Link item for vertical navigation
 */
export interface VerticalLinkItem {
  sys: { id: string };
  /** URL - used as the element ID to scroll to */
  url?: string | null;
  /** Display text */
  urlText?: string | null;
}

export interface NavQuickLinksVerticalProps {
  /** System ID */
  sys?: { id: string };
  /** Collection of link items */
  linksCollection?: {
    items: Array<VerticalLinkItem | null>;
  } | null;
  /** Optional className */
  className?: string;
}

/**
 * NavQuickLinksVertical - Vertical stepper navigation
 *
 * A sidebar navigation that shows progress through page sections.
 * Tracks scroll position to highlight the current section.
 *
 * Features:
 * - Vertical stepper dots with connecting lines
 * - Active state based on scroll position
 * - Smooth scroll to section on click
 * - Progress indication (filled dots/lines for passed sections)
 *
 * Usage:
 * - Place sections with IDs matching the `url` field of each link
 * - Component will detect which section is in view and highlight it
 */
export function NavQuickLinksVertical({ linksCollection, className }: NavQuickLinksVerticalProps) {
  const aria = useAriaLabels();
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [stepIndex, setStepIndex] = useState(-1);

  const items =
    linksCollection?.items?.filter((item): item is VerticalLinkItem => item !== null) ?? [];

  // Handle scroll to detect current section
  const handleScroll = useCallback(() => {
    items.forEach((step, index) => {
      if (!step.url) return;

      const element = document.getElementById(step.url);
      if (element) {
        const elementTop = element.getBoundingClientRect().top;
        // Consider element "active" when it's in the top 40% of viewport
        const isActive = elementTop >= 0 && elementTop <= window.innerHeight * 0.4;

        if (isActive) {
          setStepIndex(index);
          setCurrentUrl(step.url);
        }
      }
    });
  }, [items]);

  // Scroll to element when clicking a step
  const scrollToElement = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Offset for header (240px in original - adjust as needed)
      const headerOffset = 240;
      const elementTop = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: elementTop, behavior: 'smooth' });
    }
  }, []);

  // Set up scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (items.length === 0) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <nav
      className={clsx(styles.verticalStepper, 'vertical-stepper', className)}
      aria-label={aria.action('pageSectionsNavigation')}
    >
      {items.map((step, index) => (
        <button
          key={step.sys.id}
          className={clsx(
            styles.step,
            currentUrl === step.url && styles.active,
            stepIndex > index && styles.afterStep
          )}
          onClick={() => scrollToElement(step.url ?? '#')}
          type="button"
          aria-current={currentUrl === step.url ? 'step' : undefined}
        >
          <span className={styles.stepLabel}>{step.urlText}</span>
          <span className={styles.stepContainer}>
            <span className={styles.stepContent} />
            <span className={styles.stepLine} />
          </span>
        </button>
      ))}
    </nav>
  );
}

NavQuickLinksVertical.displayName = 'NavQuickLinksVertical';
